import React from 'react';
import {
  Button,
  DialogActions,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { ModalBase } from 'src/components/ConfirmationModal';
import { LcaProductFolderOutSchema } from '../types';
import {
  flattenFolderTree,
  formatFolderPath,
  getFolderPath,
} from './folderTree';

const NO_FOLDER_VALUE = 'none';

interface MoveToFolderDialogProps {
  open: boolean;
  folders: LcaProductFolderOutSchema[];
  titleKey: string;
  description: string;
  // label of the "no folder" choice, e.g. "Without folder" or "Top level"
  noFolderLabel: string;
  // where the moved item sits now; picking it again is not a move
  currentFolderId?: number | null;
  // folders that cannot be picked, e.g. a folder and its own sub-folders
  excludedFolderIds?: Set<number>;
  isSubmitting?: boolean;
  onClose: () => void;
  // eslint-disable-next-line no-unused-vars
  onSubmit: (folderId: number | null) => void;
}

export default function MoveToFolderDialog({
  open,
  folders,
  titleKey,
  description,
  noFolderLabel,
  currentFolderId,
  excludedFolderIds,
  isSubmitting = false,
  onClose,
  onSubmit,
}: MoveToFolderDialogProps) {
  const { t } = useTranslation('lca');
  const rows = flattenFolderTree(folders);
  const isExcluded = (folderId: number) => !!excludedFolderIds?.has(folderId);

  const firstSelectable = rows.find((row) => !isExcluded(row.folder.id));
  let initialValue = firstSelectable
    ? String(firstSelectable.folder.id)
    : NO_FOLDER_VALUE;
  if (currentFolderId !== undefined) {
    initialValue =
      currentFolderId === null ? NO_FOLDER_VALUE : String(currentFolderId);
  }

  const [value, setValue] = React.useState<string>(initialValue);

  React.useEffect(() => {
    if (open) setValue(initialValue);
  }, [open, initialValue]);

  const isUnchanged = currentFolderId !== undefined && value === initialValue;

  const submit = () => {
    if (isSubmitting || isUnchanged) return;
    onSubmit(value === NO_FOLDER_VALUE ? null : Number(value));
  };

  return (
    <ModalBase open={open} onClose={onClose} titleKey={titleKey}>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ textAlign: 'center', mb: '20px' }}
      >
        {description}
      </Typography>
      <FormControl sx={{ width: '100%', minWidth: '360px', maxWidth: '520px' }}>
        <InputLabel id="lca-move-to-folder-label">
          {t('folders.title')}
        </InputLabel>
        <Select
          labelId="lca-move-to-folder-label"
          label={t('folders.title')}
          value={value}
          onChange={(event) => setValue(String(event.target.value))}
          renderValue={(selected) =>
            selected === NO_FOLDER_VALUE
              ? noFolderLabel
              : formatFolderPath(getFolderPath(folders, Number(selected)))
          }
        >
          <MenuItem value={NO_FOLDER_VALUE}>{noFolderLabel}</MenuItem>
          {rows.map(({ folder, depth }) => (
            <MenuItem
              key={folder.id}
              value={String(folder.id)}
              disabled={isExcluded(folder.id)}
              sx={{ pl: `${16 + depth * 20}px` }}
            >
              {folder.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <DialogActions
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: '20px',
          paddingBottom: 0,
        }}
      >
        <Button
          onClick={onClose}
          variant="secondary"
          sx={{ minWidth: '200px' }}
        >
          <Trans i18nKey="lca:selection.moveDialog.cancel" />
        </Button>
        <Button
          onClick={submit}
          disabled={isSubmitting || isUnchanged}
          sx={{ minWidth: '200px' }}
        >
          <Trans i18nKey="lca:selection.moveDialog.submit" />
        </Button>
      </DialogActions>
    </ModalBase>
  );
}
